import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listPimCollections } from "../lib/api.js";
import type { PimCollectionItem } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { PackageIcon, HashIcon, ChevronRightIcon } from "../lib/icons.js";

const PACKAGE_TYPES: Array<{ value: string; label: string }> = [
  { value: "", label: "Všechny" },
  { value: "plastic_kit", label: "Plastic Part Kits" },
  { value: "powerwear_collection", label: "PowerWear Collections" },
  { value: "powerparts_kit", label: "PowerParts Kits" },
  { value: "promo_kit", label: "Promo Kits" },
];

/**
 * Collections & Kits — balíčky složené z více SKU.
 *   - PowerWear Collections (tričko + mikina + kšiltovka)
 *   - KTM EXC Plastic Part Kits (plastové kity per model/rok)
 *   - PowerParts Kits (kompletní CNC sady)
 */
export function CollectionsPage(): JSX.Element {
  const [search, setSearch] = useState("");
  const [packageType, setPackageType] = useState("");
  const [showDiscontinued, setShowDiscontinued] = useState(false);
  const [items, setItems] = useState<PimCollectionItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 48;

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, packageType, showDiscontinued]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPimCollections({
      search: debouncedSearch || undefined,
      packageType: packageType || undefined,
      includeDiscontinued: showDiscontinued,
      limit: LIMIT,
      offset,
    })
      .then((d) => {
        if (cancelled) return;
        setItems(d.items);
        setTotal(d.total);
      })
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, packageType, showDiscontinued, offset]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          Collections & Kits
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Balíčky: PowerWear Collections, KTM EXC Plastic Part Kits, PowerParts Kits.
          Každý je složený z více SKU.
        </p>
      </header>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <select
          value={packageType}
          onChange={(e) => setPackageType(e.target.value)}
          className="h-9 px-3 rounded-md border border-zinc-200 text-sm bg-white"
        >
          {PACKAGE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        <div className="relative">
          <HashIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat…"
            className="h-11 sm:h-9 pl-9 pr-3 rounded-md border border-zinc-200 text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none w-full sm:w-72"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={showDiscontinued}
            onChange={(e) => setShowDiscontinued(e.target.checked)}
          />
          Zobrazit i zrušené
        </label>

        <div className="ml-auto text-sm text-zinc-500">
          {total.toLocaleString("cs-CZ")} balíčků
        </div>
      </div>

      {/* Grid */}
      {loading && !items ? (
        <LoadingBlock label="Načítám…" />
      ) : !items || items.length === 0 ? (
        <EmptyState
          icon={<PackageIcon size={28} />}
          title="Žádné balíčky"
          description="Zkuste jiný filtr."
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <CollectionCard key={it.id} item={it} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="mt-6 flex items-center justify-between text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            ← Předchozí
          </Button>
          <div className="text-xs text-zinc-500">
            {offset + 1}–{Math.min(offset + LIMIT, total)} z{" "}
            {total.toLocaleString("cs-CZ")}
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            Další →
          </Button>
        </div>
      )}
    </div>
  );
}

function CollectionCard({ item }: { item: PimCollectionItem }): JSX.Element {
  return (
    <Link
      to={`/part/${encodeURIComponent(item.header.articleNumber)}`}
      className="group bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:border-ktm-orange hover:shadow-md transition-all"
    >
      <div className="aspect-square bg-zinc-50 relative overflow-hidden">
        {item.header.imageUrl ? (
          <img
            src={item.header.imageUrl}
            alt={item.header.marketingName ?? ""}
            className="w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-300">
            <PackageIcon size={40} />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <Badge tone={packageTypeTone(item.packageType)}>
            {packageTypeLabel(item.packageType)}
          </Badge>
        </div>
        <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-white/95 backdrop-blur px-2 py-1 rounded-md border border-zinc-200 text-zinc-700 text-[11px] font-semibold">
          {item.itemCount} ks v setu
        </div>
      </div>

      <div className="p-3">
        <div className="text-xs text-zinc-400 font-mono">
          {item.header.articleNumber}
        </div>
        <div className="mt-1 text-sm font-medium text-zinc-900 line-clamp-2 group-hover:text-ktm-orange transition-colors min-h-[2.5rem]">
          {item.header.marketingName ?? item.name}
        </div>
        {item.header.descriptionShort && (
          <div className="mt-1 text-xs text-zinc-500 line-clamp-2">
            {item.header.descriptionShort}
          </div>
        )}
        <div className="mt-2 flex items-center justify-end">
          <ChevronRightIcon size={14} className="text-zinc-300 group-hover:text-ktm-orange transition-colors" />
        </div>
      </div>
    </Link>
  );
}

function packageTypeLabel(t: string): string {
  return (
    {
      plastic_kit: "Plastic Kit",
      powerwear_collection: "PW Collection",
      powerparts_kit: "PP Kit",
      promo_kit: "Promo",
    }[t] ?? t
  );
}

function packageTypeTone(t: string): "orange" | "blue" | "green" | "neutral" {
  return (
    (
      {
        plastic_kit: "orange",
        powerwear_collection: "blue",
        powerparts_kit: "green",
      } as const
    )[t] ?? "neutral"
  );
}
